import { useState } from 'react';
import {
    Grid,
    Card,
    Stack,
    Divider,
    Typography,
    CardHeader,
    styled,
    Link,
    IconButton,
    Tooltip,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import BusinessIcon from '@mui/icons-material/Business';
import dayjs from 'dayjs';
import EditNoteIcon from '@mui/icons-material/EditLocationAlt';
import { When } from 'react-if';
import { useSelector } from 'react-redux';

import { RootState } from '../../store';
import Iconify from '../iconify';
import { MySubscription } from '../subscription-card';
import PlaySoundButton from '../play-sound-button';
import { LocationForm } from './location-form';
import { SetCustomerAlertSettingsForm } from './set-alert-settings-form';
import { SetPhoneForm } from './set-phone-form';

const StyledIcon = styled(Iconify)(({ theme }) => ({
    width: 20,
    height: 20,
    marginTop: 1,
    flexShrink: 0,
    marginRight: theme.spacing(2),
}));

interface Props {}

export const CustomerProfileData: React.FC<Props> = ({}) => {
    const [locationForm, setLocationForm] = useState({
        home: false,
        work: false,
    });
    const [phoneFormModalVisible, setPhoneFormModalVisible] = useState(false);
    const { authUser } = useSelector((state: RootState) => state.auth);
    const { details } = useSelector((state: RootState) => state.customer); 

    const fullWorkAddress = authUser?.workLocation ? 
        `${authUser?.workLocation?.address} ${authUser?.workLocation?.city}, ${authUser?.workLocation?.state} - ${authUser?.workLocation?.zip}`
        : 
        `${details?.customer?.city}, ${details?.customer?.state} - ${details?.customer?.zip}`;

    const fullHomeAddress = authUser?.homeLocation ? 
        `${authUser?.homeLocation?.address} ${authUser?.homeLocation?.city}, ${authUser?.homeLocation?.state} - ${authUser?.homeLocation?.zip}`
        : 
        'N/A'

    const toggleLocationFormModal = (id: keyof typeof locationForm, v: boolean) => setLocationForm(lf => ({ ...lf, [id]: v }));

    return (
        <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
                <Stack spacing={3}>
                    <Card sx={{ border: 1, borderColor: 'divider', boxShadow: (theme) => theme.shadows[2] }}>
                        <CardHeader title="About" />

                        <Stack spacing={2} sx={{ p: 3 }}>
                            
                            <Typography fontWeight={500} variant="body2">
                                Home and Work Locations:
                            </Typography>

                            <Stack direction="row" mt={2}>
                                <BusinessIcon sx={{ mr: 2 }}/>
                                    
                                <Typography variant="body2">
                                    {fullWorkAddress}
                                </Typography>
                                <Tooltip title={'Change work address'} placement='top'>
                                    <IconButton
                                        sx={{ mt: -2, ml: 1 }}
                                        aria-label="details"
                                        onClick={() => {
                                            toggleLocationFormModal('work', true);
                                        }}
                                    >
                                        <EditNoteIcon />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                            <Stack direction="row" mt={2}>
                                <HomeIcon sx={{ mr: 2 }}/>
                                    
                                <Typography variant="body2">
                                    {fullHomeAddress}
                                </Typography>
                                <Tooltip title={'Change home address'} placement='top'>
                                    <IconButton
                                        sx={{ mt: -1.5, ml: 1 }}
                                        aria-label="details"
                                        onClick={() => {
                                            toggleLocationFormModal('home', true);
                                        }}
                                    >
                                        <EditNoteIcon />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                            <Typography fontWeight={500} variant="body2">
                                My Contact Information
                            </Typography>

                            <Stack direction="row">
                                <StyledIcon icon="eva:email-fill" />
                                <Typography variant="body2">
                                    {authUser?.email}
                                </Typography>
                            </Stack>

                            <Stack direction="row">
                                <StyledIcon icon="bi:phone" />

                                <Typography variant="body2">
                                    <Link
                                        component="span"
                                        variant="subtitle2"
                                        color="text.primary"
                                    >
                                        {authUser?.phone ||
                                            '----'}
                                    </Link>
                                </Typography>
                                <Tooltip title={'Change my phone number'} placement='top'>
                                    <IconButton
                                        sx={{ mt: -1.5, ml: 1 }}
                                        aria-label="details"
                                        onClick={() => {
                                            setPhoneFormModalVisible(true);
                                        }}
                                    >
                                        <EditNoteIcon />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Stack>
                    </Card>
                </Stack>
            </Grid>
            <Grid item xs={12} md={8}>
                <Grid container xs={12} md={12} spacing={2}>
                    <Grid item xs={12} md={12}>
                        <Grid container spacing={2}>
                            {details?.subscriptions?.length > 0 &&
                                details?.subscriptions?.map((sub) => {
                                    return (
                                        <Grid item xs={12} md={12}>
                                            <MySubscription
                                                key={JSON.stringify(sub)}
                                                subscription={sub}
                                                customer={details.customer}
                                            />
                                        </Grid>
                                    );
                                })}
                        </Grid>
                    </Grid>
                    <When condition={authUser.pomMaster === false}>
                        <Grid item md={6} xs={12}>
                            <SetCustomerAlertSettingsForm />
                        </Grid>
                        <Grid item md={6} xs={12}>
                            <Card sx={{ border: 1, borderColor: 'divider', boxShadow: (theme) => theme.shadows[2] }}>
                                <CardHeader title={'Choose Map Alert Sound'} />
                                <PlaySoundButton />
                            </Card>
                        </Grid>
                    </When>
                </Grid>
            </Grid>

            
            <When condition={locationForm.home}>
                <LocationForm  
                    type='home'
                    visible={locationForm.home}
                    onClose={() => {
                        toggleLocationFormModal('home', false);
                    }}
                />
            </When>

            <When condition={locationForm.work}>
                <LocationForm  
                    type='work'
                    visible={locationForm.work}
                    onClose={() => {
                        toggleLocationFormModal('work', false);
                    }}
                />
            </When>

            <When condition={phoneFormModalVisible}>
                <SetPhoneForm
                    visible={phoneFormModalVisible}
                    onClose={() => {
                        setPhoneFormModalVisible(false);
                    }}
                />
            </When>
        </Grid>
    );
};
